<script setup lang="ts">
import { ref, nextTick, watch, onMounted, onUnmounted, computed } from 'vue'
import LoadingSpinner from './LoadingSpinner.vue'
import SuggestionsDropdown from './SuggestionsDropdown.vue'
import ZenModeView from './ZenModeView.vue'
// HighlightModal import removed - citations disabled
import CorpusModal from './CorpusModal.vue'
import PhilosopherHeader from './PhilosopherHeader.vue'
import * as Sentry from '@sentry/vue'
import { usePhilosopherStore } from '../stores/philosopher'
import { trackEvent } from '../utils/analytics'
import { getLeftBio, getRightBio } from '../utils/philosopher'
import { promptService } from '../services/PromptService'
import { useZenMode } from '../composables/useZenMode'
import { philosopherChatService } from '../services/PhilosopherChatService'

// Get philosophers from the store
const philosopherStore = usePhilosopherStore()

// Define bios using imported utility functions
const leftBio = ref(getLeftBio())
const rightBio = ref(getRightBio())

// Use reactive store values for display names and alignments
const LEFT_DISPLAY_NAME = philosopherStore.leftPhilosopher.name
const LEFT_ALIGNMENT = philosopherStore.leftPhilosopher.alignment
const RIGHT_DISPLAY_NAME = philosopherStore.rightPhilosopher.name
const RIGHT_ALIGNMENT = philosopherStore.rightPhilosopher.alignment

// For mobile view
const isMobile = ref(window.innerWidth <= 768)

// Initialize Zen mode
const { isZenMode, toggleZenMode } = useZenMode()

// Track window width for responsive design
const isMobileView = ref(window.innerWidth <= 480)
function updateIsMobileView() {
  isMobileView.value = window.innerWidth <= 480
}

// Track fullscreen state
const isFullScreen = ref(!!document.fullscreenElement)
function updateFullScreenState() {
  isFullScreen.value = !!document.fullscreenElement
}

onMounted(() => {
  window.addEventListener('resize', updateIsMobileView)
  document.addEventListener('fullscreenchange', updateFullScreenState)
})

onUnmounted(() => {
  window.removeEventListener('resize', updateIsMobileView)
  document.removeEventListener('fullscreenchange', updateFullScreenState)
})

// Computed properties for Zen mode view
const leftPhilosopherName = computed(() => LEFT_DISPLAY_NAME)
const rightPhilosopherName = computed(() => RIGHT_DISPLAY_NAME)

// Control visibility of historical context section
const showHistoricalContext = ref(false)

// Watch for changes to context visibility and track in Sentry
watch(showHistoricalContext, (isVisible) => {
  if (isVisible) {
    try {
      // Track with Segment.io instead of Sentry
      trackEvent('Historical Context Viewed', {
        action: "show_historical_context",
        mobile: isMobile.value,
        timestamp: new Date().toISOString()
      });
    } catch (err) {
      // Silent catch to prevent tracking failures from affecting app functionality
      console.error('Failed to track context view event:', err);
    }
  }
});

// Function to track when philosopher bio is viewed
function trackPhilosopherView(philosopher: string, alignment: string, method: string) {
  try {
    // Track with Segment.io instead of Sentry
    trackEvent('View Philosopher Bio', {
      philosopher: philosopher,
      alignment: alignment,
      view_method: method, // 'hover' or 'click'
      mobile: isMobile.value,
      timestamp: new Date().toISOString()
    });
  } catch (err) {
    // Silent catch to prevent tracking failures from affecting app functionality
    console.error('Failed to track philosopher view event:', err);
  }
}

// Add window resize listener
onMounted(() => {
  window.addEventListener('resize', () => {
    isMobile.value = window.innerWidth <= 768
  })
})

interface Message {
  text: string
  isUser: boolean
  questionIndex?: number
  loading?: boolean
  citations?: Citation[]
  highlights?: Highlight[]
  suggestedReadings?: string[]
}

interface Citation {
  title: string
  url?: string
  text: string
  id?: string
}

interface Highlight {
  text: string
  citation_id: string
}

// Messages for the left side
const leftSideMessages = ref<Message[]>([])

// Messages for the right side
const rightSideMessages = ref<Message[]>([])

// Shared user input
const newMessage = ref('')

// Track all user questions
const userQuestions = ref<string[]>([])

// Token tracking removed

// For clipboard functionality
const copySuccess = ref(false)

// References to containers
const mainScrollContainer = ref<HTMLElement | null>(null)
const lastMessageMarker = ref<HTMLElement | null>(null)
const inputRef = ref<HTMLInputElement | null>(null)

// Track scroll position and loading state for scroll buttons
const isAtBottom = ref(false)
const isLastMessageLoading = ref(false)

// Highlight modal removed - citations disabled

// Suggested questions - fetched from Cloudflare Worker
const suggestedQuestions = ref<string[]>([])
const showSuggestions = ref(false)

// Function to handle selecting a suggestion
function selectSuggestion(suggestion: string) {
  newMessage.value = suggestion
  showSuggestions.value = false
  
  // Track suggestion selection in Segment.io instead of Sentry
  try {
    trackEvent('Canned question selected', {
      suggestion: suggestion
    });
  } catch (err) {
    // Silent catch to prevent tracking failures from affecting app functionality
    console.error('Failed to track suggestion selection:', err);
  }
  
  // Focus the input field
  if (inputRef.value) {
    inputRef.value.focus()
  }
}

// Helper function to add citation markers to text
function addCitationMarkers(text: string): string {
  // Citations disabled - format paragraphs and process basic markdown
  // Split by double newlines and wrap in spans with paragraph class
  return text.split('\n\n')
    .map(para => {
      let processed = para.trim();
      
      // First escape HTML to prevent XSS
      processed = processed
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
      
      // Then process basic markdown formatting
      // Bold: **text** or __text__
      processed = processed.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
      processed = processed.replace(/__([^_]+)__/g, '<strong>$1</strong>');
      
      // Italic: *text* or _text_ (but not within words)
      processed = processed.replace(/(?<!\w)\*([^*]+)\*(?!\w)/g, '<em>$1</em>');
      processed = processed.replace(/(?<!\w)_([^_]+)_(?!\w)/g, '<em>$1</em>');
      
      return processed ? `<span class="paragraph">${processed}</span>` : '';
    })
    .filter(p => p !== '')
    .join('');
}

// Citations disabled - ApiResponse interface removed

// Helper function to extract citations from API response
// Citations disabled - function removed

// Removed unused suggested readings functions - now handled by PhilosopherChatService

// Citations disabled - extractHighlights function removed

// Author constants
const LEFT_AUTHOR = 'Adam Smith'
const RIGHT_AUTHOR = 'Karl Marx'

// Ideological alignments for philosophers
const LEFT_AUTHOR_ALIGNMENT = 'Classical Liberalism, Scottish Enlightenment'
const RIGHT_AUTHOR_ALIGNMENT = 'Socialism, Marxism'

// Corpus modal states and data
const showLeftCorpusModal = ref(false)
const showRightCorpusModal = ref(false)
// Static predefined titles for each philosopher
const leftCorpusTitles = ref<string[]>(['An Inquiry into the Nature and Causes of the Wealth of Nations'])
const rightCorpusTitles = ref<string[]>(['The Communist Manifesto'])
const loadingLeftCorpus = ref(false)
const loadingRightCorpus = ref(false)
const corpusError = ref<string | null>(null)

// Title summary state - kept for backward compatibility with tests
const showLeftTitleSummary = ref(false)
const showRightTitleSummary = ref(false)
const currentLeftTitleSummary = ref<{ title: string; summary: string } | null>(null)
const currentRightTitleSummary = ref<{ title: string; summary: string } | null>(null)
const loadingLeftSummary = ref(false)
const loadingRightSummary = ref(false)

// Initialize the philosopher store with default values
philosopherStore.setPhilosophers(
  { name: LEFT_AUTHOR, alignment: LEFT_AUTHOR_ALIGNMENT },
  { name: RIGHT_AUTHOR, alignment: RIGHT_AUTHOR_ALIGNMENT }
)

// Function to "fetch" corpus titles for a philosopher
// Since we're using static data, this just simulates a loading state
function fetchCorpusTitles(assistantId: string, side: string) {
  if (side === LEFT_AUTHOR) {
    loadingLeftCorpus.value = true
    corpusError.value = null
    
    // Simulate a short loading delay
    setTimeout(() => {
      loadingLeftCorpus.value = false
    }, 500)
  } else {
    loadingRightCorpus.value = true
    corpusError.value = null
    
    // Simulate a short loading delay
    setTimeout(() => {
      loadingRightCorpus.value = false
    }, 500)
  }
}

// This functionality has been moved to the CorpusModal component
// Kept for backward compatibility with tests
// eslint-disable-next-line @typescript-eslint/no-unused-vars
async function requestTitleSummary(title: string, side: string) {
  // Determine which data to use based on side
  if (side === LEFT_AUTHOR) {
    loadingLeftSummary.value = true
    showLeftTitleSummary.value = true

    try {
      // Track the summary request
      try {
        trackEvent('Summary requested', { side });
      } catch (err) {
        console.error('Failed to track summary request event:', err);
      }

      // Use the new service to get the summary
      const summary = await philosopherChatService.getCorpusSummary(title, 'adam-smith')
      
      // Store the summary based on side
      currentLeftTitleSummary.value = { title, summary }
      loadingLeftSummary.value = false
      
      // Track summary view in Sentry
      try {
        Sentry.setContext("title_summary", {
          philosopher: LEFT_DISPLAY_NAME,
          title: title,
          timestamp: new Date().toISOString()
        });
        
        Sentry.captureMessage('Title summary viewed', {
          level: 'info'
        });
      } catch (err) {
        console.error('Failed to track title summary view event:', err);
      }
    } catch (error) {
      // Handle error and reset loading state
      console.error('Error requesting title summary:', error)
      
      currentLeftTitleSummary.value = { 
        title, 
        summary: 'Sorry, there was an error fetching the summary. Please try again.' 
      }
      loadingLeftSummary.value = false
    }
  } else {
    loadingRightSummary.value = true
    showRightTitleSummary.value = true

    try {
      // Track the summary request
      try {
        trackEvent('Summary requested', { side });
      } catch (err) {
        console.error('Failed to track summary request event:', err);
      }

      // Use the new service to get the summary
      const summary = await philosopherChatService.getCorpusSummary(title, 'karl-marx')
      
      // Store the summary based on side
      currentRightTitleSummary.value = { title, summary }
      loadingRightSummary.value = false
      
      // Track summary view in Sentry
      try {
        Sentry.setContext("title_summary", {
          philosopher: RIGHT_DISPLAY_NAME,
          title: title,
          timestamp: new Date().toISOString()
        });
        
        Sentry.captureMessage('Title summary viewed', {
          level: 'info'
        });
      } catch (err) {
        console.error('Failed to track title summary view event:', err);
      }
    } catch (error) {
      // Handle error and reset loading state
      console.error('Error requesting title summary:', error)
      
      currentRightTitleSummary.value = { 
        title, 
        summary: 'Sorry, there was an error fetching the summary. Please try again.' 
      }
      loadingRightSummary.value = false
    }
  }
}

// Function to go back to corpus list from summary view - kept for backward compatibility with tests
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function backToCorpusList(side: string) {
  if (side === LEFT_AUTHOR) {
    showLeftTitleSummary.value = false
    currentLeftTitleSummary.value = null
  } else {
    showRightTitleSummary.value = false
    currentRightTitleSummary.value = null
  }
}

// Functions to toggle corpus modals
function toggleLeftCorpusModal() {
  showLeftCorpusModal.value = !showLeftCorpusModal.value
}

function toggleRightCorpusModal() {
  showRightCorpusModal.value = !showRightCorpusModal.value
}

// Function to check if the last message is still loading
function checkIfLastMessageLoading() {
  if (userQuestions.value.length === 0) {
    isLastMessageLoading.value = false;
    return;
  }
  
  const lastQuestionIndex = userQuestions.value.length - 1;
  
  // Check if either left or right message for the last question is still loading
  const leftLoading = leftSideMessages.value.find(
    m => m.questionIndex === lastQuestionIndex && m.loading
  );
  
  const rightLoading = rightSideMessages.value.find(
    m => m.questionIndex === lastQuestionIndex && m.loading
  );
  
  isLastMessageLoading.value = !!(leftLoading || rightLoading);
}

// Function to check if scrolled to bottom
function checkScrollPosition() {
  if (!mainScrollContainer.value) return;
  
  const container = mainScrollContainer.value;
  const atBottom = (container.scrollHeight - container.scrollTop - container.clientHeight) < 50;
  isAtBottom.value = atBottom;
  
  // Also check loading state
  checkIfLastMessageLoading();
}

// Function to scroll to the top of the last message
function scrollToLastMessageTop() {
  const lastConversation = document.getElementById('last-conversation');
  if (lastConversation) {
    lastConversation.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

// Function to scroll to the bottom
function scrollToBottom() {
  nextTick(() => {
    if (lastMessageMarker.value) {
      // Use smooth scrolling for better user experience
      lastMessageMarker.value.scrollIntoView({ behavior: 'smooth', block: 'end' })
    } else if (mainScrollContainer.value) {
      // Fallback to direct container scrolling with animated behavior
      mainScrollContainer.value.scrollTo({
        top: mainScrollContainer.value.scrollHeight,
        behavior: 'smooth'
      })
    }
  })
}

// Instead of auto-scrolling, we now check scroll position when messages update
watch(
  [leftSideMessages, rightSideMessages, userQuestions],
  () => {
    nextTick(() => {
      checkScrollPosition();
    });
  },
  { deep: true },
)

// Bio visibility tracking is now handled by the PhilosopherHeader component

// Function to copy text to clipboard
async function copyToClipboard(text: string, event?: MouseEvent) {
  console.log('ChatApp: copyToClipboard called', { text, event })
  try {
    console.log('ChatApp: Before calling navigator.clipboard.writeText')
    await navigator.clipboard.writeText(text)
    console.log('ChatApp: Text copied successfully')
    copySuccess.value = true
    
    // Show visual feedback only if event is provided
    if (event && event.currentTarget) {
      const button = event.currentTarget as HTMLElement;
      const originalInnerHTML = button.innerHTML;
      button.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="20 6 9 17 4 12"></polyline>
        </svg>
      `;
      
      // Reset success indicator and restore original icon after 1.5 seconds
      setTimeout(() => {
        copySuccess.value = false
        if (button) {
          button.innerHTML = originalInnerHTML;
        }
      }, 1500)
    } else {
      // If no event provided, just reset the success indicator after delay
      setTimeout(() => {
        copySuccess.value = false
      }, 1500)
    }
  } catch (err: unknown) {
    console.error('ChatApp: Failed to copy text: ', err)
    if (err instanceof Error) {
      console.log('ChatApp: Error details:', { name: err.name, message: err.message, stack: err.stack })
    }
  }
}

// Function to show the highlight modal when a source is clicked
// Citations disabled - showSourceHighlight function removed

// Initial setup on mount
onMounted(async () => {
  // Add scroll event listener to track position
  if (mainScrollContainer.value) {
    mainScrollContainer.value.addEventListener('scroll', checkScrollPosition);
    // Initial check of scroll position
    checkScrollPosition();
  }

  // Fetch prompts from Cloudflare Worker
  try {
    suggestedQuestions.value = await promptService.getPrompts();
  } catch (err) {
    console.error('Failed to fetch prompts:', err);
    // Service already provides fallback prompts in case of error
  }

  // Track debate prompts generation in Segment.io
  try {
    trackEvent('Debate prompts generated', {
      prompt_count: suggestedQuestions.value.length,
      mobile: isMobile.value
    });
  } catch (err) {
    // Silent catch to prevent tracking failures
    console.error('Failed to track debate prompts generation:', err);
  }

  // Inject paragraph indentation style to override any other styles
  const styleEl = document.createElement('style')
  styleEl.textContent = `
    .message p {
      text-indent: 2em !important;
      margin-top: 0.7em !important;
      margin-bottom: 0.7em !important;
    }
  `
  document.head.appendChild(styleEl)
})

// Remove event listeners on unmount to prevent memory leaks
onUnmounted(() => {
  if (mainScrollContainer.value) {
    mainScrollContainer.value.removeEventListener('scroll', checkScrollPosition);
  }
})

// Query the appropriate assistant based on side (left or right)
async function queryAssistant(query: string, side: string) {
  try {
    // Determine which philosopher to query based on side
    const philosopher = side === LEFT_AUTHOR ? 'adam-smith' : 'karl-marx'
    
    // Use the new PhilosopherChatService
    const response = await philosopherChatService.chat(query, philosopher, 0.7)

    // Token tracking removed

    // Transform response to match expected format
    return {
      data: {
        message: {
          content: response.content
        },
        citations: response.citations.map((citation, index) => ({
          text: citation.text,
          title: citation.title,
          id: `citation-${index}`,
          relevance: citation.relevance,
          // Include other properties that might be needed
        })),
        usage: response.usage
      },
      suggestedReadings: response.suggestedReadings
    }
  } catch (error) {
    // console.error('Error querying assistant:', error)
    throw error
  }
}

// Send message function
async function sendMessage() {
  // Check if input is empty
  if (newMessage.value.trim().length === 0) return;
  
  // Only track successful button clicks in Sentry
  try {
    // Set event context that will be included with any errors that occur
    Sentry.setContext("button_click", {
      button_id: 'ask-button',
      button_name: 'Ask',
      input_length: newMessage.value.length,
      timestamp: new Date().toISOString()
    });
    
    // Also capture specific button click event for analytics
    Sentry.captureMessage('Ask button clicked', {
      level: 'info'
    });
  } catch (err) {
    // Silent catch to prevent tracking failures from affecting app functionality
    console.error('Failed to track button click:', err);
  }

  // Token counter reset removed

  const userQuestion = newMessage.value
  const questionIndex = userQuestions.value.length

  // Add user question to tracking array
  userQuestions.value.push(userQuestion)

  // Clear input and hide suggestions
  newMessage.value = ''
  showSuggestions.value = false
  
  // Scroll to bottom when user sends a new message
  scrollToBottom()

  // Add left side loading message
  leftSideMessages.value.push({
    text: '',
    isUser: false,
    loading: true,
    questionIndex,
  })
  
  // Update loading state
  isLastMessageLoading.value = true;

  // Add right side loading message
  rightSideMessages.value.push({
    text: '',
    isUser: false,
    loading: true,
    questionIndex,
  })

  // Fetch responses from separate assistants in parallel
  try {
    const [leftResponse, rightResponse] = await Promise.all([
      queryAssistant(userQuestion, LEFT_AUTHOR),
      queryAssistant(userQuestion, RIGHT_AUTHOR),
    ])

    // Process left side response
    const leftIndex = leftSideMessages.value.findIndex(
      (m) => m.questionIndex === questionIndex && m.loading,
    )
    if (leftIndex !== -1) {
      // Debug the entire response structure with author filter info
      // console.log(`Left side (${LEFT_AUTHOR}) full response:`, JSON.stringify(leftResponse.data))

      // Extract content safely
      const rawContent = leftResponse.data.message?.content || 'No content available'
      
      // Process citations from new RAG service format
      const citations = leftResponse.data.citations || [];
      
      // Don't pre-process content with HTML - let the template handle rendering
      const content = rawContent;
      
      // Create highlights from the citation text content
      const highlights = citations.map((citation: Citation) => ({
        text: citation.text || 'No highlight available for this source.',
        citation_id: citation.id || 'unknown'
      }));

      // Suggested readings disabled
      const suggestedReadings: string[] = [];

      leftSideMessages.value[leftIndex] = {
        text: content,
        isUser: false,
        questionIndex,
        loading: false,
        citations: citations,
        highlights: highlights,
        suggestedReadings: suggestedReadings,
      }
    }

    // Process right side response
    const rightIndex = rightSideMessages.value.findIndex(
      (m) => m.questionIndex === questionIndex && m.loading,
    )
    if (rightIndex !== -1) {
      // Debug the entire response structure with author filter info
      // console.log(`Right side (${RIGHT_AUTHOR}) full response:`, JSON.stringify(rightResponse.data))

      // Extract content safely
      const rawContent = rightResponse.data.message?.content || 'No content available'
      
      // Process citations from new RAG service format
      const citations = rightResponse.data.citations || [];
      
      // Don't pre-process content with HTML - let the template handle rendering
      const content = rawContent;
      
      // Create highlights from the citation text content
      const highlights = citations.map((citation: Citation) => ({
        text: citation.text || 'No highlight available for this source.',
        citation_id: citation.id || 'unknown'
      }));

      // Suggested readings disabled
      const suggestedReadings: string[] = [];

      rightSideMessages.value[rightIndex] = {
        text: content,
        isUser: false,
        questionIndex,
        loading: false,
        citations: citations,
        highlights: highlights,
        suggestedReadings: suggestedReadings,
      }
    }

    // Check if all messages are loaded
    checkIfLastMessageLoading();
    
    // scrollToBottom() - Disabled automatic scrolling when responses are received
  } catch {
    // Handle errors gracefully without using the error object
    
    // Update messages to show error state
    const errorMessage = 'Sorry, there was an error fetching the response. Please try again.'

    const leftIndex = leftSideMessages.value.findIndex(
      (m) => m.questionIndex === questionIndex && m.loading,
    )
    if (leftIndex !== -1) {
      leftSideMessages.value[leftIndex] = {
        text: errorMessage,
        isUser: false,
        questionIndex,
        loading: false,
      }
    }

    const rightIndex = rightSideMessages.value.findIndex(
      (m) => m.questionIndex === questionIndex && m.loading,
    )
    if (rightIndex !== -1) {
      rightSideMessages.value[rightIndex] = {
        text: errorMessage,
        isUser: false,
        questionIndex,
        loading: false,
      }
    }
    
    // Update loading state after error handling
    checkIfLastMessageLoading();
  }
}
</script>

<template>
  <div class="outer-container">
    <!-- Logo area -->
    <div class="logo-container">
      <div class="logo-placeholder">
        <div class="logo-content">
          <span class="logo-emoji left-emoji">🤌</span>
          <span class="logo-text">oui beef</span>
          <span class="logo-emoji right-emoji">🥩</span>
          <span class="tagline">The truth is<br>somewhere in the middle</span>
        </div>
      </div>
    </div>
    
    <!-- Historical context section -->
    <div class="context-container">
      <button 
        class="context-toggle" 
        @click="showHistoricalContext = !showHistoricalContext"
        :class="{ 'active': showHistoricalContext }"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="context-icon">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="16" x2="12" y2="12"></line>
          <line x1="12" y1="8" x2="12.01" y2="8"></line>
        </svg>
        <span>{{ showHistoricalContext ? 'Hide the beef' : 'Where\'s the beef?' }}</span>
      </button>
      
      <div v-if="showHistoricalContext" class="historical-context">
        <p>
          Adam Smith and Karl Marx represent a classic intellectual rivalry in economic and political thought. Smith, often called the "father of capitalism," advocated for free markets, individual liberty, and the "invisible hand" guiding economic prosperity in The Wealth of Nations (1776). Marx, conversely, critiqued capitalism's inequalities, proposing a class struggle leading to socialism and communism in works like The Communist Manifesto (1848) and Das Kapital. Their rivalry symbolizes the enduring debate between market-driven individualism and collectivist critiques of exploitation, shaping modern economic systems and ideologies.
        </p>
      </div>
    </div>
    
    <!-- Removed top-level Zen Mode toggle button -->
    
    <!-- Zen Mode Overlay -->
    <div v-if="isZenMode" class="zen-overlay" @click="toggleZenMode"></div>

    <!-- Fixed header with perspective names -->
    <div class="header-area">
      <div class="left-header">
        <PhilosopherHeader 
          side="left"
          :display-name="LEFT_DISPLAY_NAME"
          :alignment="LEFT_ALIGNMENT"
          :bio="leftBio"
          :assistant-id="'adam-smith'"
          :corpus-titles="leftCorpusTitles"
          avatar-image="https://miro.medium.com/v2/resize:fit:1080/1*vdLiCkirBPYebcgI2jkAHA.png"
          :is-mobile="isMobile"
          @toggle-corpus-modal="toggleLeftCorpusModal"
          @view-philosopher="trackPhilosopherView"
        />
      </div>
      <div class="right-header">
        <PhilosopherHeader 
          side="right"
          :display-name="RIGHT_DISPLAY_NAME" 
          :alignment="RIGHT_ALIGNMENT"
          :bio="rightBio"
          :assistant-id="'karl-marx'"
          :corpus-titles="rightCorpusTitles"
          avatar-image="https://upload.wikimedia.org/wikipedia/commons/thumb/d/d4/Karl_Marx_001.jpg/330px-Karl_Marx_001.jpg"
          :is-mobile="isMobile"
          @toggle-corpus-modal="toggleRightCorpusModal"
          @view-philosopher="trackPhilosopherView"
        />
      </div>
    </div>

    <!-- Main scrollable content area with questions and answers -->
    <div ref="mainScrollContainer" class="unified-scroll-container">
      <!-- Zen Mode View -->
      <div v-if="isZenMode" class="zen-mode-view">
        <ZenModeView 
          :leftMessages="leftSideMessages"
          :rightMessages="rightSideMessages"
          :userQuestions="userQuestions"
          :leftPhilosopherName="leftPhilosopherName"
          :rightPhilosopherName="rightPhilosopherName"
          @toggle-zen-mode="toggleZenMode"
        />
      </div>
      <!-- Scroll to bottom button - hidden while spinner is visible or in Zen mode -->
      <button 
        v-if="userQuestions.length > 0 && !isLastMessageLoading && !isZenMode" 
        @click="scrollToBottom()" 
        class="scroll-to-bottom-button"
        title="Scroll to bottom"
      >
        ↓
      </button>
      
      <!-- Scroll to top of last message button - only visible when at the bottom, not loading, and not in Zen mode -->
      <button 
        v-if="isAtBottom && userQuestions.length > 0 && !isLastMessageLoading && !isZenMode" 
        @click="scrollToLastMessageTop()" 
        class="scroll-to-top-button"
        title="Scroll to top of message"
      >
        ↑
      </button>
      
      <!-- Conversation entries -->
      <div
        v-for="(question, qIndex) in userQuestions"
        :key="'entry-' + qIndex"
        class="conversation-entry"
        :id="qIndex === userQuestions.length - 1 ? 'last-conversation' : ''"
      >
        <!-- User question -->
        <div class="user-question">
          {{ question }}
        </div>

        <!-- Response area -->
        <div class="responses">
          <!-- Left side response -->
          <div class="response-container">
            <div
              v-if="leftSideMessages.findIndex((m) => m.questionIndex === qIndex) >= 0"
              class="message left-message"
              :class="{ 'older-message': qIndex < userQuestions.length - 1 }"
            >
              <div
                v-if="leftSideMessages.find((m) => m.questionIndex === qIndex)?.loading"
                class="loading-indicator"
              >
                <LoadingSpinner />
              </div>
              <!-- Text rendering with footnotes -->
              <div v-else class="text-content">
                <!-- Copy icon button -->
                <div class="copy-icon-container">
                  <button 
                    class="copy-button" 
                    @click="(event) => copyToClipboard(leftSideMessages.find((m) => m.questionIndex === qIndex)?.text || '', event)"
                    title="Copy to clipboard"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                    </svg>
                  </button>
                  <button 
                    v-if="!leftSideMessages.find((m) => m.questionIndex === qIndex)?.loading && isMobileView && !isFullScreen"
                    class="zen-mode-toggle"
                    @click="toggleZenMode"
                    title="Zen Reading Mode"
                    aria-label="Toggle Zen Reading Mode"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="zen-icon">
                      <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
                    </svg>
                  </button>
                </div>
                
                <!-- Process text with footnote markers if citations exist -->
                <div
                  v-if="leftSideMessages.find((m) => m.questionIndex === qIndex)?.citations?.length"
                  class="paragraphs-with-footnotes"
                  v-html="addCitationMarkers(
                    leftSideMessages.find((m) => m.questionIndex === qIndex)?.text || ''
                  )"
                ></div>

                <!-- Regular text rendering without footnotes -->
                <template v-else>
                  <span
                    v-for="(paragraph, index) in leftSideMessages
                      .find((m) => m.questionIndex === qIndex)
                      ?.text.split('\n\n')"
                    :key="index"
                    class="paragraph"
                  >
                    {{ paragraph }}
                  </span>
                </template>

                <!-- Citations section removed -->
                
                <!-- Suggested Readings section -->
                <div
                  v-if="leftSideMessages.find((m) => m.questionIndex === qIndex)?.suggestedReadings?.length"
                  class="suggested-readings-container"
                >
                  <h4 class="suggested-readings-header">Suggested Readings</h4>
                  <ul class="suggested-readings-list">
                    <li
                      v-for="(reading, idx) in leftSideMessages.find((m) => m.questionIndex === qIndex)?.suggestedReadings"
                      :key="'left-reading-' + idx"
                      class="suggested-reading-item"
                    >
                      {{ reading }}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div v-else class="message-placeholder">
              <!-- Loading placeholder if no message yet -->
              <div v-if="qIndex === userQuestions.length - 1" class="typing-indicator">
                <span></span><span></span><span></span>
              </div>
            </div>
          </div>

          <!-- Right side response -->
          <div class="response-container">
            <div
              v-if="rightSideMessages.findIndex((m) => m.questionIndex === qIndex) >= 0"
              class="message right-message"
              :class="{ 'older-message': qIndex < userQuestions.length - 1 }"
            >
              <div
                v-if="rightSideMessages.find((m) => m.questionIndex === qIndex)?.loading"
                class="loading-indicator"
              >
                <LoadingSpinner />
              </div>
              <!-- Text rendering with footnotes -->
              <div v-else class="text-content">
                <!-- Copy icon button -->
                <div class="copy-icon-container">
                  <button 
                    class="copy-button" 
                    @click="(event) => copyToClipboard(rightSideMessages.find((m) => m.questionIndex === qIndex)?.text || '', event)"
                    title="Copy to clipboard"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                    </svg>
                  </button>
                  <button 
                    v-if="!rightSideMessages.find((m) => m.questionIndex === qIndex)?.loading && isMobileView && !isFullScreen"
                    class="zen-mode-toggle"
                    @click="toggleZenMode"
                    title="Zen Reading Mode"
                    aria-label="Toggle Zen Reading Mode"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="zen-icon">
                      <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path>
                    </svg>
                  </button>
                </div>
                
                <!-- Process text with footnote markers if citations exist -->
                <div
                  v-if="rightSideMessages.find((m) => m.questionIndex === qIndex)?.citations?.length"
                  class="paragraphs-with-footnotes"
                  v-html="addCitationMarkers(
                    rightSideMessages.find((m) => m.questionIndex === qIndex)?.text || ''
                  )"
                ></div>

                <!-- Regular text rendering without footnotes -->
                <template v-else>
                  <span
                    v-for="(paragraph, index) in rightSideMessages
                      .find((m) => m.questionIndex === qIndex)
                      ?.text.split('\n\n')"
                    :key="index"
                    class="paragraph"
                  >
                    {{ paragraph }}
                  </span>
                </template>

                <!-- Citations section removed -->
                
                <!-- Suggested Readings section -->
                <div
                  v-if="rightSideMessages.find((m) => m.questionIndex === qIndex)?.suggestedReadings?.length"
                  class="suggested-readings-container"
                >
                  <h4 class="suggested-readings-header">Suggested Readings</h4>
                  <ul class="suggested-readings-list">
                    <li
                      v-for="(reading, idx) in rightSideMessages.find((m) => m.questionIndex === qIndex)?.suggestedReadings"
                      :key="'right-reading-' + idx"
                      class="suggested-reading-item"
                    >
                      {{ reading }}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div v-else class="message-placeholder">
              <!-- Loading placeholder if no message yet -->
              <div v-if="qIndex === userQuestions.length - 1" class="typing-indicator">
                <span></span><span></span><span></span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Bottom marker for auto-scrolling -->
      <div ref="lastMessageMarker" class="last-message-marker"></div>
    </div>

    <!-- Token usage display -->
    <!-- Token display removed -->

    <!-- Fixed input area -->
    <div class="input-area">
      <div class="input-container">
        <input
          ref="inputRef"
          v-model="newMessage"
          type="text"
          placeholder="Ask a question..."
          @keyup.enter="sendMessage"
          @input="newMessage.length > 0 ? showSuggestions = false : showSuggestions = true"
          @focus="showSuggestions = true"
        />
        <SuggestionsDropdown
          :suggestions="suggestedQuestions"
          :input-element="inputRef"
          :is-visible="showSuggestions && newMessage === ''"
          @select="selectSuggestion"
          @outside-click="showSuggestions = false"
        />
      </div>
      <button 
        @click="sendMessage" 
        @mousedown="showSuggestions = false"
        :disabled="newMessage.trim().length === 0"
        id="ask-button"
      >Ask</button>
    </div>
    
    <!-- Highlight Modal removed - citations disabled -->
    
    <!-- Left Corpus Modal -->
    <CorpusModal
      side="left"
      :is-visible="showLeftCorpusModal"
      :philosopher-name="LEFT_DISPLAY_NAME"
      :philosopher-alignment="LEFT_ALIGNMENT"
      :assistant-id="'adam-smith'"
      :corpus-titles="leftCorpusTitles"
      @close="showLeftCorpusModal = false"
      @fetch-titles="fetchCorpusTitles"
    />
    
    <!-- Right Corpus Modal -->
    <CorpusModal
      side="right"
      :is-visible="showRightCorpusModal"
      :philosopher-name="RIGHT_DISPLAY_NAME"
      :philosopher-alignment="RIGHT_ALIGNMENT"
      :assistant-id="'karl-marx'"
      :corpus-titles="rightCorpusTitles"
      @close="showRightCorpusModal = false"
      @fetch-titles="fetchCorpusTitles"
    />
  </div>
</template>

<style>
/* Styles have been extracted to assets/components/ChatApp.css */
</style>
